/**
 * The one hook every screen uses. Gives the live state, the connection status, a server-synced
 * clock, and `send` for events. Mock mode (?mock=1) swaps the websocket for the in-browser fake.
 */

import { useEffect, useMemo, useSyncExternalStore } from "react";
import type { Player, TournamentEvent } from "../../../engine/types";
import type { PinKind, PinMode } from "../../../shared/pins";
import type { AdminName, Answer, Role } from "../../../shared/protocol";
import { PING_MS, TournamentClient, type Snapshot } from "./client";
import { MockTransport } from "./mock";
import { Keeper } from "./session";
import { WsTransport, wsUrl } from "./transport";

export interface Screen {
  role: Role;
  token: string;
  court?: number;
  tournament: string;
  mock: boolean;
  speed: number;
}

export interface Tournament extends Snapshot {
  /** Server time now. Use this for every countdown. */
  now(): number;
  send(event: TournamentEvent, opts?: { queue?: boolean; id?: string }): string;
  regenerate(role: Role): void;
  askLinks(): void;
  /** Admin only: the named admins (decision 38). */
  addAdmin(name: string): void;
  removeAdmin(id: string): void;
  renewAdmin(id: string): void;
  /** Admin and watcher: ask for the last events of the log; they arrive as `log`. */
  askLog(last: number): void;
  reset(): void;
  /** Send a player's picture. Resolves to null when stored, or the reason it was not. */
  uploadPhoto(playerId: string, jpeg: Blob): Promise<string | null>;
  /** A player's picture for a Face, or null when they have none. */
  faceOf(player: Player | undefined): string | null;
  // ---- PINs (decisions 39 and 40)
  /** Does this player have a PIN yet? */
  pinCheck(player: string): Promise<Answer>;
  /** Set or type a player's PIN; the right one makes this phone that player. */
  claim(player: string, pin: string, mode: PinMode): Promise<Answer>;
  /** Set or type this admin screen's PIN. */
  adminPin(pin: string, mode: PinMode): Promise<Answer>;
  /** The hidden way in: the named admins who have set a PIN. */
  adminNames(): Promise<Answer & { admins?: AdminName[] }>;
  /** The hidden way in: an admin PIN, for a picked admin or the admin link this phone opened before. */
  adminOpen(pin: string, to: { admin: string } | { link: string }): Promise<Answer>;
  /** Admin only: reset a player's or an admin's PIN. */
  resetPin(as: PinKind, id: string): Promise<Answer>;
  /** "Not you?": this phone forgets who it is. */
  forget(): void;
  clearSignedOut(): void;
  /** The owner's link was replaced from this phone: its admin session moves with it. */
  moveAdminLink(token: string): void;
  /** The owner's Note key: ask the wall for its picture for the note `rid` (v26.10.027, temporary). */
  noteAskWall(rid: string): boolean;
  /** Where this phone keeps its sessions for this tournament. */
  keeper: Keeper;
  who: Screen;
}

const clients = new Map<string, TournamentClient>();

function clientFor(screen: Screen, by: string): TournamentClient {
  const key = `${screen.tournament}|${screen.role}|${screen.token}|${screen.court ?? 0}|${screen.mock}`;
  let client = clients.get(key);
  if (!client) {
    const identity = { role: screen.role, tournament: screen.tournament, ...(screen.court ? { court: screen.court } : {}) };
    const transport = screen.mock
      ? new MockTransport({ identity, token: screen.token, speed: screen.speed, gameMs: 90_000, changeoverMs: 20_000 })
      : new WsTransport(wsUrl(screen.role, screen.token, screen.court, screen.tournament));
    client = new TournamentClient(
      transport,
      { role: screen.role, tournament: screen.tournament, token: screen.token, ...(screen.court ? { court: screen.court } : {}) },
      by,
      screen.mock ? screen.speed : 1,
      // players and admins keep a session per tournament (decisions 39 and 40)
      screen.role === "player" || screen.role === "admin" ? keeperFor(screen) : null,
    );
    clients.set(key, client);
    client.start();
  }
  client.by = by;
  return client;
}

/** This phone's sessions for the screen's tournament (the pretend day keeps its own). */
export function keeperFor(screen: Pick<Screen, "tournament" | "mock">): Keeper {
  return new Keeper(screen.tournament, screen.mock);
}

export function useTournament(screen: Screen, by?: string): Tournament {
  const actor = by ?? (screen.role === "court" ? `court-${screen.court}` : screen.role);
  const client = useMemo(() => clientFor(screen, actor), [screen.role, screen.token, screen.court, screen.tournament, screen.mock, screen.speed, actor]);
  const snap = useSyncExternalStore(
    (fn) => client.subscribe(fn),
    () => client.get(),
    () => client.get(),
  );
  useEffect(() => {
    // the ping is also the watchdog's beat (client.ts): a dead line shows Offline within two of them
    const t = setInterval(() => client.ping(), PING_MS);
    return () => clearInterval(t);
  }, [client]);
  return {
    ...snap,
    now: () => client.now(),
    send: (event, opts) => client.send(event, opts),
    regenerate: (role) => client.regenerate(role),
    askLinks: () => client.askLinks(),
    addAdmin: (name) => client.addAdmin(name),
    removeAdmin: (id) => client.removeAdmin(id),
    renewAdmin: (id) => client.renewAdmin(id),
    askLog: (last) => client.askLog(last),
    reset: () => client.reset(),
    uploadPhoto: (playerId, jpeg) => client.uploadPhoto(playerId, jpeg),
    faceOf: (player) => (player && player.photo > 0 ? client.photoUrl(player.id, player.photo) : null),
    pinCheck: (player) => client.pinCheck(player),
    claim: (player, pin, mode) => client.claim(player, pin, mode),
    adminPin: (pin, mode) => client.adminPin(pin, mode),
    adminNames: () => client.adminNames(),
    adminOpen: (pin, to) => client.adminOpen(pin, to),
    resetPin: (as, id) => client.resetPin(as, id),
    forget: () => client.forget(),
    clearSignedOut: () => client.clearSignedOut(),
    moveAdminLink: (token) => client.moveAdminLink(token),
    noteAskWall: (rid) => client.noteAskWall(rid),
    keeper: client.keeper ?? keeperFor(screen),
    who: screen,
  };
}

/** Which screen this page is, from its address. */
export function screenFromLocation(): Screen | null {
  const parts = location.pathname.split("/").filter(Boolean);
  const q = new URLSearchParams(location.search);
  const mock = q.get("mock") === "1";
  const speed = Math.max(1, Number(q.get("speed") ?? "1") || 1);
  const tournament = q.get("t") ?? "main";
  const [kind, token, third] = parts;
  if (!kind || !token) return null;
  const base = { token, tournament, mock, speed };
  switch (kind) {
    case "p":
      return { role: "projector", ...base };
    case "c": {
      const court = Number(third);
      if (!Number.isInteger(court) || court < 1) return null;
      return { role: "court", court, ...base };
    }
    case "a":
      return { role: "admin", ...base };
    case "j":
      return { role: "player", ...base };
    default:
      return null;
  }
}
